import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";

import { get } from "lib/api";
import { isCustomerRole, isDistributorRole, isPortalRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import { useAuth } from "context/AuthContext";
import MxShell from "../components/MxShell";
import {
  AccountIdentity,
  AccountListGroup,
  AccountListRow,
  QuickActionBar,
  buildAccountQuickActions,
  portalChrome,
} from "mobile/portal/PortalAccountChrome";
import { mx } from "../lib/theme";

function usePortalSession() {
  const auth = useAuth();
  const role = String(auth.pluginRole || "").toLowerCase();
  return {
    ...auth,
    role,
    isPortal: isPortalRole(role),
    isCustomer: isCustomerRole(role),
    isDistributor: isDistributorRole(role),
  };
}

export default function PortalHome() {
  const session = usePortalSession();
  const navigate = useNavigate();
  const { t } = useStorefrontCopy();

  const meQuery = useQuery({
    queryKey: ["mx-mobile-portal-me"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!session.token && session.isPortal,
    staleTime: 15000,
  });

  if (!session.token) return <Navigate to="/m/authentication/sign-in" replace />;
  if (session.authLoading || session.pluginRole == null) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", bgcolor: mx.canvas }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (!session.isPortal) return <Navigate to="/m/post-login" replace />;

  const customer = meQuery.data?.data || null;
  const profile = meQuery.data?.profile || null;
  const displayName = customer?.name || profile?.displayName || t("portal.accountHome", "Cuenta");
  const email = profile?.email || customer?.emails?.[0] || "";
  const roleLabel = session.isDistributor
    ? t("portal.distributor", "Distribuidor")
    : t("portal.customer", "Cliente");
  const quickActions = buildAccountQuickActions({
    onLogout: () => {
      session.logout();
      navigate("/m", { replace: true });
    },
  }).map((action) => {
    const labels = {
      home: t("common.home", "Inicio"),
      cart: t("common.cart", "Carrito"),
      scan: t("registeredUnits.scanTitle", "Escanear"),
      logout: t("common.signOut", "Cerrar sesión"),
    };
    return { ...action, label: labels[action.key] || action.label };
  });

  return (
    <MxShell
      showLogo
      rightAction={
        <ButtonBase
          onClick={() => navigate("/m/catalog")}
          sx={{
            px: 1.25,
            py: 0.7,
            borderRadius: "999px",
            bgcolor: portalChrome.accentSoft,
            color: portalChrome.accentHover,
            fontSize: 13,
            fontWeight: 750,
          }}
        >
          {t("common.shop", "Tienda")}
        </ButtonBase>
      }
    >
      <Box sx={{ px: 2, pb: 4, maxWidth: 560, mx: "auto" }}>
        <AccountIdentity name={displayName} email={email} roleLabel={roleLabel} />
        <QuickActionBar actions={quickActions} />

        {session.isDistributor ? (
          <AccountListGroup title={t("portal.distributorTools", "Distribuidor")}>
            <AccountListRow
              icon="rule"
              title={t("portal.distributorRequests", "Solicitudes de pedido")}
              subtitle={t("portal.distributorRequestsDetail", "Apruebe solicitudes o envíelas a VBS.")}
              onClick={() => navigate("/m/account/distributor-requests")}
            />
            <AccountListRow
              icon="groups"
              title={t("portal.linkedCustomers", "Clientes vinculados")}
              subtitle={t("portal.linkedCustomersDetail", "Clientes asignados a su cuenta.")}
              onClick={() => navigate("/m/account/distributor-customers")}
              divider={false}
            />
          </AccountListGroup>
        ) : null}

        <AccountListGroup title={t("portal.ordersAndService", "Pedidos y servicio")}>
          <AccountListRow
            icon="receipt_long"
            title={t("portal.orders", "Pedidos")}
            subtitle={t("portal.ordersHint", "Estado, totales, facturas y rastreo.")}
            onClick={() => navigate("/m/account/orders")}
          />
          <AccountListRow
            icon="qr_code_scanner"
            title={
              session.isDistributor
                ? t("registeredUnits.distributorTitle", "Unidades vendidas")
                : t("portal.registeredUnits", "Cuerpos de válvulas")
            }
            subtitle={
              session.isDistributor
                ? t("registeredUnits.distributorSubtitle", "Rastree unidades vendidas y registro.")
                : t("registeredUnits.subtitle", "Garantía, cores y registros.")
            }
            onClick={() => navigate("/m/account/valve-bodies")}
          />
          {!session.isDistributor ? (
            <AccountListRow
              icon="replay"
              title={t("portal.coreReturns", "Devolución de cores")}
              subtitle={t(
                "coreReturn.hubSubtitle",
                "Devuelva cores adeudados con una etiqueta prepaga."
              )}
              onClick={() => navigate("/m/account/core-returns")}
            />
          ) : null}
          <AccountListRow
            icon="forum"
            title={t("portal.support", "Soporte")}
            subtitle={t("portal.supportHint", "Chat con VBS — mensajes y fotos.")}
            onClick={() => navigate("/m/account/support")}
            divider={false}
          />
        </AccountListGroup>

        <AccountListGroup title={t("portal.accountTools", "Cuenta")}>
          <AccountListRow
            icon="person"
            title={t("portal.profile", "Perfil")}
            subtitle={t("portal.profileHint", "Contacto e información de cuenta.")}
            onClick={() => navigate("/m/account/profile")}
          />
          <AccountListRow
            icon="location_on"
            title={t("portal.addresses", "Direcciones")}
            subtitle={t("portal.addressesHint", "Direcciones de envío guardadas.")}
            onClick={() => navigate("/m/account/addresses")}
            divider={false}
          />
        </AccountListGroup>

        <AccountListGroup title={t("common.shopSection", "Tienda")}>
          <AccountListRow
            icon="shopping_bag"
            title={t("portal.browseCatalog", "Ver catálogo")}
            subtitle={t("catalog.subtitle", "Productos disponibles con precios actuales.")}
            onClick={() => navigate("/m/catalog")}
          />
          <AccountListRow
            icon="shopping_cart"
            title={t("common.cart", "Carrito")}
            subtitle={t("cart.emptyText", "Revise artículos del catálogo.")}
            onClick={() => navigate("/m/cart")}
            divider={false}
          />
        </AccountListGroup>

        <AccountListGroup>
          <AccountListRow
            icon="logout"
            title={t("common.signOut", "Cerrar sesión")}
            danger
            divider={false}
            onClick={() => {
              session.logout();
              navigate("/m", { replace: true });
            }}
          />
        </AccountListGroup>
      </Box>
    </MxShell>
  );
}
